import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../lib/content";

const LINES = ["BREAK", "THE CYCLE", "MOVEMENT"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden bg-[#090A0F] grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={IMAGES.shattered} alt="Shattered glass catching light in darkness" className="w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0F]/80 via-[#090A0F]/50 to-[#090A0F]" />
      </motion.div>

      <div className="absolute top-1/4 right-[8%] w-40 h-40 border border-[#EF4444]/30 rotate-12 clipped-frame hidden lg:block" />
      <div className="absolute bottom-1/4 left-[6%] w-24 h-24 border border-white/10 -rotate-6 clipped-frame hidden lg:block" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 pt-40 pb-28 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
          className="mono-label text-[#EF4444] mb-6"
        >
          Community campaign · Ōtautahi Christchurch · Aotearoa New Zealand
        </motion.p>

        <h1 data-testid="hero-headline" className="font-display font-extrabold uppercase leading-[0.92] tracking-tight text-white text-5xl sm:text-7xl lg:text-[7.5rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.16, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
              >
                {line === "THE CYCLE" ? <span className="text-[#EF4444]">THE CYCLE</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 text-lg sm:text-xl text-slate-300 max-w-xl font-grotesk"
        >
          No to meth. <span className="text-white font-bold">It leads to death.</span>
          <span className="block mt-2 text-slate-400 text-base">Christchurch communities are strongest when no one faces meth harm alone. No shame. No judgement. Confidential support, when you're ready.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a data-testid="hero-cta-support" href="#whanau" className="bg-[#EF4444] hover:bg-[#DC2626] transition-colors text-white font-bold rounded-full px-7 py-3.5 text-sm">
            Get support now
          </a>
          <a data-testid="hero-cta-explore" href="#truth" className="border border-white/25 hover:border-white/60 hover:bg-white/5 transition-all text-white font-bold rounded-full px-7 py-3.5 text-sm">
            Learn the facts
          </a>
          <span className="mono-label text-slate-500">#NoToMeth · #BreakTheCycle · #SaveLives</span>
        </motion.div>
      </motion.div>

      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500">
        <ArrowDown size={20} />
      </motion.div>
    </section>
  );
}
